import * as React from 'react';
import styled from 'styled-components';

import { AiColor16, QuietHours } from 'ui/icons';
import { colors, fontSizes, linkColors, spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { Skeleton } from 'ui/skeleton';
import { Link } from 'ui/links';

import { to12HourClock } from 'utils/time';
import {
  WorkflowCampaignNodeMetadata,
  WorkflowIdData,
  WorkflowNodeCampaign,
  WorkflowTimestampData,
  WorkflowTypeData,
} from '../../../types';
import { COLLAPSED_CAMPAIGN_LIST_MAX_LENGTH, SKELETON_CAMPAIGN_COUNT } from '../../../constants';
import { useCampaignsByWorkflowNodeId } from '../../../hooks/useCampaignsByWorkflowNodeId';
import { useAttachedWorkflowNodeIds } from '../../../hooks/useAttachedWorkflowNodeIds';
import { useCampaignThumbnails } from '../../../hooks/useCampaignThumbnails';

import {
  CampaignTemplateThumbnail,
  CampaignTemplateThumbnailSkeleton,
} from './CampaignTemplateThumbnail';
import { LinkButton } from './LinkButton';
import {
  workflowCampaignUsesFrequencyOptimization,
  workflowCampaignUsesSendTimeOptimization,
} from './utils';
import { CampaignInsightsLinks } from './CampaignInsightsLinks';

const { useState, useCallback, useMemo } = React;

const displayText = {
  id: 'ID',
  showAll: 'Show all',
  showLess: 'Show less',
  detached: 'detached',
  sendTimeOptimized: 'Send time optimized',
  frequencyOptimized: 'Frequency optimized',
  sendTimeAndFrequencyOptimized: 'Send time and frequency optimized',
  quietHours: 'This campaign uses Quiet Hours. Sends will be paused between',
};

const shouldShowDataScienceOptimizationIcon = (campaign: WorkflowNodeCampaign) =>
  workflowCampaignUsesSendTimeOptimization(campaign) ||
  workflowCampaignUsesFrequencyOptimization(campaign);

const getDataScienceOptimizationTooltipText = (campaign: WorkflowNodeCampaign) => {
  const usesSendTimeOptimization = workflowCampaignUsesSendTimeOptimization(campaign);
  const usesFrequencyOptimization = workflowCampaignUsesFrequencyOptimization(campaign);
  if (usesSendTimeOptimization && usesFrequencyOptimization) {
    return displayText.sendTimeAndFrequencyOptimized;
  } else if (usesSendTimeOptimization) {
    return displayText.sendTimeOptimized;
  } else if (usesFrequencyOptimization) {
    return displayText.frequencyOptimized;
  }
  return '';
};

const List = styled.ul`
  padding: 0;
  margin: ${spacing.base} 0 0 0;
  max-height: ${spacing.x32};
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: ${spacing.base};
`;

const CampaignListItem = styled.li`
  display: grid;
  grid-auto-columns: 1fr;
  grid-template-columns: min-content 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 0 ${spacing.half};
  grid-template-areas:
    'thumbnail name'
    'thumbnail id';
  margin-bottom: ${spacing.base};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const CampaignNameWrapper = styled.div`
  grid-area: name;
  text-decoration: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const CampaignName = styled.a`
  font-size: ${fontSizes.s14};
  color: ${colors.textPrimary};
  text-decoration: none;

  && {
    &:hover {
      color: ${linkColors.linkHover};
    }

    &:active {
      color: ${linkColors.linkActive};
    }

    &:focus {
      color: ${linkColors.linkFocus};
      outline: none;
    }
  }
`;

const CampaignId = styled.div`
  grid-area: id;
  font-size: ${fontSizes.s12};
  color: ${colors.textSecondary};
`;

const CampaignListItemIconTooltip = styled(Tooltip)`
  vertical-align: text-top;
  margin-right: ${spacing.eighth};
  display: inline-flex;
`;

const CampaignTemplateThumbnailContainer = styled.div`
  grid-area: thumbnail;
`;

const LinksContainer = styled.div`
  display: flex;
  align-items: baseline;
`;

const TextSkeleton = styled(Skeleton)`
  grid-area: campaignCount;
  height: ${fontSizes.s16};
  background-color: ${colors.grey5};
  margin-right: ${spacing.half};
  align-self: center;
  width: ${(props: { skeletonWidth: number }) =>
    props.skeletonWidth ? `${props.skeletonWidth}%` : spacing.x7};
`;

const getQuietHoursTooltipContent = (campaign: WorkflowNodeCampaign) => {
  if (campaign.customQuietHours) {
    const startTime = to12HourClock(campaign?.customQuietHours?.startTime as string);
    const endTime = to12HourClock(campaign?.customQuietHours?.endTime as string);
    return `${displayText.quietHours} ${startTime} and ${endTime}`;
  }

  return '';
};

type CampaignsListProps = {
  workflow: WorkflowIdData & WorkflowTimestampData & WorkflowTypeData;
  workflowCampaignNodeMetadata: WorkflowCampaignNodeMetadata;
  isVisible: boolean;
};

export const CampaignsList = ({
  workflow,
  workflowCampaignNodeMetadata,
  isVisible,
}: CampaignsListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const expand = useCallback(() => setIsExpanded(true), []);
  const collapse = useCallback(() => setIsExpanded(false), []);

  const campaignsByWorkflowNodeId = useCampaignsByWorkflowNodeId(workflowCampaignNodeMetadata);
  const attachedWorkflowNodeIds = useAttachedWorkflowNodeIds(workflowCampaignNodeMetadata);
  const campaignIdsForAttachedWorkflowNodes = useMemo(
    () =>
      attachedWorkflowNodeIds.reduce((previousValue, attachedNodeId) => {
        if (campaignsByWorkflowNodeId[attachedNodeId]) {
          previousValue.add(campaignsByWorkflowNodeId[attachedNodeId].id);
        }
        return previousValue;
      }, new Set<number>()),
    [attachedWorkflowNodeIds],
  );
  const campaigns = useMemo(
    () =>
      Object.values(campaignsByWorkflowNodeId).sort((campaignA, campaignB) => {
        if (
          campaignIdsForAttachedWorkflowNodes.has(campaignA.id) ===
          campaignIdsForAttachedWorkflowNodes.has(campaignB.id)
        ) {
          return campaignA.id - campaignB.id;
        }
        return campaignIdsForAttachedWorkflowNodes.has(campaignA.id) ? -1 : 1;
      }),
    [campaignsByWorkflowNodeId, campaignIdsForAttachedWorkflowNodes],
  );

  const canExpand = campaigns.length > COLLAPSED_CAMPAIGN_LIST_MAX_LENGTH;
  const truncatedCampaigns = useMemo(
    () =>
      isExpanded || !canExpand ? campaigns : campaigns.slice(0, COLLAPSED_CAMPAIGN_LIST_MAX_LENGTH),
    [campaigns, isExpanded || !canExpand],
  );

  /** When campaign list is visible, fetch/cache thumbnails for visible campaigns */
  useCampaignThumbnails(isVisible ? truncatedCampaigns : []);

  return (
    <>
      <List>
        {truncatedCampaigns.map((campaign) => (
          <CampaignListItem data-test="workflow-campaignlist-item" key={campaign.id}>
            <CampaignTemplateThumbnailContainer>
              <CampaignTemplateThumbnail campaign={campaign} />
            </CampaignTemplateThumbnailContainer>
            <CampaignNameWrapper>
              <Link
                inTable
                data-test="workflow-campaign-name"
                href={`/workflows/${workflow.id}/edit?mode=beta&workflowType=${workflow.workflowType}&nodeId=${campaign.nodeId}`}
              >
                {campaign.name}
              </Link>
            </CampaignNameWrapper>
            <CampaignId data-test="workflow-campaign-id">
              {shouldShowDataScienceOptimizationIcon(campaign) && (
                <CampaignListItemIconTooltip
                  tooltipContent={getDataScienceOptimizationTooltipText(campaign)}
                >
                  <AiColor16 data-test="workflow-campaign-data-science-optimization-icon" />
                </CampaignListItemIconTooltip>
              )}{' '}
              {campaign?.customQuietHours && (
                <CampaignListItemIconTooltip tooltipContent={getQuietHoursTooltipContent(campaign)}>
                  <QuietHours
                    data-test="workflow-campaign-quiet-hours-icon"
                    width={16}
                    height={16}
                  />
                </CampaignListItemIconTooltip>
              )}
              {displayText.id}: {campaign.id}{' '}
              {!campaignIdsForAttachedWorkflowNodes.has(campaign.id) && `(${displayText.detached})`}
            </CampaignId>
          </CampaignListItem>
        ))}
      </List>
      <LinksContainer>
        {canExpand && (
          <LinkButton
            data-test="campaigns-list-expand-button"
            onClick={isExpanded ? collapse : expand}
          >
            {isExpanded ? displayText.showLess : `${displayText.showAll} ${campaigns.length}`}
          </LinkButton>
        )}
        <CampaignInsightsLinks campaigns={campaigns} workflow={workflow} />
      </LinksContainer>
    </>
  );
};

export const CampaignsListSkeleton = () => {
  const skeletonCampaigns = useMemo(
    () =>
      new Array(SKELETON_CAMPAIGN_COUNT).fill(null).map((_, i) => (
        <CampaignListItem key={i}>
          <CampaignTemplateThumbnailContainer>
            <CampaignTemplateThumbnailSkeleton />
          </CampaignTemplateThumbnailContainer>
          <CampaignName as="div">
            <TextSkeleton skeletonWidth={66} shimmerSpeed={1000} />
          </CampaignName>
          <CampaignId>
            <TextSkeleton skeletonWidth={33} shimmerSpeed={1000} />
          </CampaignId>
        </CampaignListItem>
      )),
    [],
  );

  return <List data-test="campaign-list-skeleton">{skeletonCampaigns}</List>;
};
